import { normaliseRegistration, normaliseTrack } from '@/lib/parse';
import type { NormalisedTrack, ParsedTrack } from '@/lib/parse';

import type { AircraftTrace, TraceFix } from './adsb-trace';
import { splitTraceIntoFlights, traceFixes } from './adsb-trace';
import { addDays, clampToReach, findEarliestDay, formatDay, utcDay } from './history';
import { fetchJsonOrNothing, headExists, SourceError } from './http';
import type {
  CostEstimate,
  HistoryReach,
  SearchQuery,
  SourceCapabilities,
  TrackCandidate,
  TrackSource,
} from './types';
import { daysInRange } from './windows';

/**
 * The community ADS-B aggregators — adsb.lol, airplanes.live, and anything else
 * running the same readsb globe.
 *
 * `DATA-SOURCES.md` §3: free, several of them favour people who feed data, and
 * they differ in history and terms rather than in shape — so this is one
 * adapter with a base URL, not three adapters. `ADSB_AGGREGATORS` holds what
 * actually differs, including the licence line the UI shows, because "free"
 * and "unencumbered" are not the same thing and attribution is not optional.
 *
 * What makes this one cheap to use and awkward to implement is that a search
 * *is* a download: the aggregators publish one file per aircraft per day, so
 * probing a date range means one request per day, and each request that hits
 * returns the whole track. That is why `search` caches what it fetched and
 * `fetch` reads out of that cache — asking for the same day twice would be a
 * second download of a file this adapter already has.
 */

export interface AdsbAggregator {
  id: string;
  label: string;
  /** Globe host, no trailing slash. */
  baseUrl: string;
  /** Licence and terms, in one line, shown wherever this source is offered. */
  terms: string;
  docsUrl: string;
  /**
   * Where days older than the live globe keeps can still be had, in one line,
   * when there is such a place. Shown only once a search runs out of history.
   */
  archive?: string;
}

export const ADSB_AGGREGATORS: readonly AdsbAggregator[] = [
  {
    id: 'adsb-lol',
    label: 'adsb.lol',
    baseUrl: 'https://globe.adsb.lol',
    terms: 'Open data under ODbL 1.0. Attribution required for redistribution.',
    docsUrl: 'https://api.adsb.lol/docs',
    /*
     * github.com/adsblol/globe_history_<year>: one release per UTC day from
     * 2023-02-16, a dump of the same /var/globe_history directory the live
     * globe serves. Each day is the whole world, several gigabytes split
     * across tar parts (about 1.7 TB for 2026 so far), which is why it is
     * named here rather than searched from a browser.
     */
    archive:
      'Older days are in the adsb.lol archive on GitHub (globe_history), not searchable here.',
  },
  {
    id: 'airplanes-live',
    label: 'airplanes.live',
    baseUrl: 'https://globe.airplanes.live',
    terms: 'Free for personal, non-commercial use under the operator’s own terms.',
    docsUrl: 'https://airplanes.live/api-guide/',
  },
];

/**
 * Each probed day is its own HTTP request, so a range has to have an end.
 *
 * This was 31 until the search could report progress and be cancelled. The
 * cap existed because a year-long search was a few minutes of silent,
 * uninterruptible requests; with a counter on screen and a Cancel button a
 * year is a reasonable thing to ask for, and "last year's flying" is the
 * question a pilot catching up a logbook actually has. A leap year is 366.
 */
export const ADSB_MAX_SEARCH_DAYS = 366;

/**
 * Day files requested at once.
 *
 * One at a time made a year take several minutes; unbounded would be several
 * hundred simultaneous requests to a free service run by volunteers, behind a
 * Cloudflare that already challenges the relay as a bot when it is pushed.
 * Four is most of the speed-up. Results are still consumed in day order, so
 * the refused-in-a-row count and the "days to … were checked" message mean
 * exactly what they did when the search was sequential. If the globes start
 * challenging long searches, this is the number to turn down.
 */
export const ADSB_SEARCH_CONCURRENCY = 4;

/**
 * Where the retention probe starts looking. Earlier than either globe's
 * history begins (adsb.lol's archive starts 2023-02-16), so a globe that kept
 * everything reads as "back to at least" this day rather than being cut short.
 */
export const ADSB_PROBE_FLOOR = '2022-01-01';

const CAPABILITIES: SourceCapabilities = {
  search: true,
  historyDays: null,
  needsKey: false,
  metered: false,
};

export interface AdsbSourceOptions {
  aggregator: AdsbAggregator;
  fetchImpl?: typeof globalThis.fetch;
  /** Fixed so a test is not a moving target. */
  importedAt?: string;
  newId?: () => string;
  now?: () => number;
  /** Waits between a refused day and its one retry. Injected so a test does not sleep. */
  sleep?: (ms: number) => Promise<void>;
  /**
   * Where a measured reach is remembered across page loads. Defaults to
   * `localStorage`; `null` turns it off, which is what tests want.
   */
  reachStore?: Pick<Storage, 'getItem' | 'setItem'> | null;
}

/** How long to wait before retrying a refused day when the source names no time. */
export const ADSB_RETRY_DELAY_MS = 3_000;
/**
 * Refused days in a row before a search gives up. One refused file is skipped;
 * a run of them is the source refusing the client, and asking on through the
 * rest of a long range would only dig that hole deeper.
 */
export const ADSB_MAX_REFUSED_IN_A_ROW = 3;
/** A `Retry-After` longer than this is not worth holding a search open for. */
const ADSB_MAX_RETRY_DELAY_MS = 15_000;

function defaultReachStore(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function normaliseHex(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^0-9a-f]/g, '');
}

/**
 * The globe's own path for one aircraft on one day.
 *
 * The `xx` shard is the *last* two characters of the hex, which is how readsb
 * fans a few hundred thousand aircraft across directories. Getting it wrong is
 * a uniform 404 that reads exactly like "this aircraft never flew".
 */
export function tracePath(hex: string, day: string): string {
  const shard = hex.slice(-2);
  const [year, month, date] = day.split('-');
  return `/globe_history/${year ?? ''}/${month ?? ''}/${date ?? ''}/traces/${shard}/trace_full_${hex}.json`;
}

/**
 * A small file readsb writes into every day's history directory: the first
 * half-hour of that day's heatmap (`globe_index.c`, `heatmap/%02d.bin.ttf`).
 *
 * It exists for every day the globe still holds, whether or not any given
 * aircraft flew, which is what makes it the right probe: a missing trace file
 * means either "did not fly" or "no longer kept", and only this tells them
 * apart. It is asked for by `HEAD`, so nothing is downloaded.
 */
export function heatmapProbePath(day: string): string {
  const [year, month, date] = day.split('-');
  return `/globe_history/${year ?? ''}/${month ?? ''}/${date ?? ''}/heatmap/00.bin.ttf`;
}

/** `12 Oct 2025 – 24 Oct 2025, 2 Nov 2025`: consecutive days folded into ranges. */
export function dayRanges(days: readonly string[]): string {
  const sorted = [...days].sort();
  const ranges: string[] = [];
  let start: string | undefined;
  let end: string | undefined;
  const close = () => {
    if (start !== undefined && end !== undefined) {
      ranges.push(start === end ? formatDay(start) : `${formatDay(start)} – ${formatDay(end)}`);
    }
  };
  for (const day of sorted) {
    if (end !== undefined && addDays(end, 1) === day) {
      end = day;
      continue;
    }
    close();
    start = day;
    end = day;
  }
  close();
  return ranges.join(', ');
}

interface CachedSegment {
  trace: AircraftTrace;
  fixes: TraceFix[];
}

export class AdsbSource implements TrackSource {
  readonly id: string;
  readonly label: string;
  readonly capabilities = CAPABILITIES;
  readonly aggregator: AdsbAggregator;

  private readonly options: AdsbSourceOptions;
  /** `ref` → the segment a previous `search` already downloaded and split out. */
  private readonly cache = new Map<string, CachedSegment>();
  /** One measurement per adapter: retention moves by a day a day, not per search. */
  private reachPromise: Promise<HistoryReach> | undefined;

  constructor(options: AdsbSourceOptions) {
    this.options = options;
    this.aggregator = options.aggregator;
    this.id = options.aggregator.id;
    this.label = options.aggregator.label;
  }

  private get base(): string {
    return this.aggregator.baseUrl.replace(/\/+$/, '');
  }

  private async fetchDay(
    hex: string,
    day: string,
    signal?: AbortSignal,
  ): Promise<AircraftTrace | undefined> {
    return fetchJsonOrNothing<AircraftTrace>(`${this.base}${tracePath(hex, day)}`, {
      service: this.aggregator.label,
      ...(signal === undefined ? {} : { signal }),
      ...(this.options.fetchImpl === undefined ? {} : { fetchImpl: this.options.fetchImpl }),
    });
  }

  /**
   * How far back this globe's history reaches, measured.
   *
   * Neither adsb.lol nor airplanes.live publishes a retention period, and
   * readsb itself never deletes a day, so the window is whatever the operator
   * keeps on disk — and it is shorter than it looks: a pilot searching a couple
   * of months back got a list of errors and empty days with nothing to say the
   * date was the reason. So it is measured: yesterday's probe file first (today's
   * may not be written yet), then a binary search back to `ADSB_PROBE_FLOOR`.
   * About a dozen `HEAD` requests, once per adapter.
   *
   * A globe that does not answer the probe at all — a different layout, a
   * relay that refuses `HEAD`, no connection — is reported as unknown rather
   * than as "no history", and searches go ahead as they did before.
   */
  history(): Promise<HistoryReach> {
    this.reachPromise ??= this.rememberedReach() ?? this.measureReach();
    return this.reachPromise;
  }

  /*
   * The probe is about a dozen requests, and every one of them counts toward
   * whatever rate limit the globe's CDN applies — the same limit the search
   * then runs into. Once measured, the answer is good for the rest of the UTC
   * day, so it is kept in `localStorage` (a per-browser convenience; losing it
   * costs one more probe) rather than re-measured on every visit.
   */
  private get reachKey(): string {
    return `fdv.sources.${this.aggregator.id}.reach`;
  }

  private get store(): Pick<Storage, 'getItem' | 'setItem'> | null {
    return this.options.reachStore === undefined ? defaultReachStore() : this.options.reachStore;
  }

  private today(): string {
    return utcDay(new Date(this.options.now?.() ?? Date.now()));
  }

  private rememberedReach(): Promise<HistoryReach> | undefined {
    try {
      const raw = this.store?.getItem(this.reachKey);
      if (raw === null || raw === undefined) {
        return undefined;
      }
      const saved = JSON.parse(raw) as { checkedDay?: unknown; reach?: HistoryReach };
      if (saved.checkedDay !== this.today() || typeof saved.reach?.summary !== 'string') {
        return undefined;
      }
      return Promise.resolve(saved.reach);
    } catch {
      return undefined;
    }
  }

  private async measureReach(): Promise<HistoryReach> {
    const label = this.aggregator.label;
    const today = this.today();
    const exists = (day: string) =>
      headExists(`${this.base}${heatmapProbePath(day)}`, {
        service: label,
        ...(this.options.fetchImpl === undefined ? {} : { fetchImpl: this.options.fetchImpl }),
      });

    let earliestDay: string | undefined;
    try {
      earliestDay = await findEarliestDay(exists, ADSB_PROBE_FLOOR, addDays(today, -1));
    } catch {
      earliestDay = undefined;
    }
    if (earliestDay === undefined) {
      // Not cached as a failure for good: the next call measures again.
      this.reachPromise = undefined;
      return { summary: `${label} does not publish how far back its history goes.` };
    }
    const reach =
      earliestDay === ADSB_PROBE_FLOOR
        ? {
            earliestDay,
            summary: `${label} holds data back to at least ${formatDay(earliestDay)}.`,
          }
        : { earliestDay, summary: `${label} holds data from ${formatDay(earliestDay)}.` };
    try {
      this.store?.setItem(this.reachKey, JSON.stringify({ checkedDay: today, reach }));
    } catch {
      // Storage full or blocked: the next visit measures again.
    }
    return reach;
  }

  async search(query: SearchQuery): Promise<TrackCandidate[]> {
    const hex = query.icaoHex === undefined ? '' : normaliseHex(query.icaoHex);
    if (hex === '') {
      throw new SourceError(
        'unsupported',
        `${this.aggregator.label} stores tracks by ICAO 24-bit address, not registration. Add the aircraft's hex code to its record, or type one here.`,
      );
    }

    const reach = await this.history();
    const from = clampToReach(query.from, query.to, reach.earliestDay);
    if (from === undefined) {
      throw new SourceError(
        'unsupported',
        [reach.summary, this.aggregator.archive].filter((line) => line !== undefined).join(' '),
      );
    }

    const days = daysInRange(from, query.to, ADSB_MAX_SEARCH_DAYS + 1);
    if (days.length > ADSB_MAX_SEARCH_DAYS) {
      throw new SourceError(
        'unsupported',
        `${this.aggregator.label} searches one day per request. Maximum range is ${ADSB_MAX_SEARCH_DAYS} days.`,
      );
    }

    const candidates: TrackCandidate[] = [];
    const newestFirst = (list: TrackCandidate[]) =>
      list.sort((a, b) => (b.startTime ?? '').localeCompare(a.startTime ?? ''));
    /*
     * A day the globe does not hold (403, see `fetchDayForSearch`) or
     * refuses for some other reason is skipped, not the end of the search, and
     * named once it finishes. Stopping at the first one threw away every day
     * after a gap in adsb.lol's history.
     *
     * What still stops it: a rate limit that outlasted its retry, a Cloudflare
     * challenge (every day after it would be challenged too), and a run of
     * other refusals long enough that the source is clearly refusing the
     * client. Missing days never count toward that run.
     */
    const refused: { day: string; error: SourceError }[] = [];
    const unavailable: string[] = [];
    let refusedInARow = 0;
    const stopWith = (error: SourceError, index: number): never => {
      const checked =
        index === 0 ? '' : ` Days to ${formatDay(days[index - 1] ?? '')} were checked.`;
      throw new SourceError(error.kind, `${error.message}${checked}`, {
        ...(error.status === undefined ? {} : { status: error.status }),
        ...(error.detail === undefined ? {} : { detail: error.detail }),
        challenged: error.challenged,
        partial: newestFirst([...candidates]),
        cause: error,
      });
    };
    /*
     * Up to `ADSB_SEARCH_CONCURRENCY` days are requested ahead of the one
     * being read, but they are read strictly in order, so everything below
     * behaves as the sequential loop it was. `stop` cancels the look-ahead
     * the moment the loop ends for any reason — a cancel, a rate limit, a
     * challenge — so none of those is answered with more requests. The
     * `.catch` only marks a look-ahead that is never read as handled; the
     * loop still awaits the original and sees its error.
     */
    const stop = new AbortController();
    // A function rather than the property, so the compiler does not carry the
    // loop-top check's narrowing across the `await` that can change it.
    const cancelled = () => query.signal?.aborted === true;
    const onAbort = () => {
      stop.abort();
    };
    query.signal?.addEventListener('abort', onAbort, { once: true });
    const pending: Promise<AircraftTrace | undefined | 'unavailable'>[] = [];
    const requestUpTo = (last: number) => {
      while (pending.length <= Math.min(last, days.length - 1) && !stop.signal.aborted) {
        const request = this.fetchDayForSearch(hex, days[pending.length] ?? '', stop.signal);
        request.catch(() => undefined);
        pending.push(request);
      }
    };

    try {
      for (const [index, day] of days.entries()) {
        if (cancelled()) {
          break;
        }
        requestUpTo(index + ADSB_SEARCH_CONCURRENCY - 1);
        let trace: AircraftTrace | undefined;
        try {
          const answer = await (
            pending[index] ?? this.fetchDayForSearch(hex, day, stop.signal)
          ).finally(() => {
            query.onProgress?.(index + 1, days.length);
          });
          refusedInARow = 0;
          if (answer === 'unavailable') {
            unavailable.push(day);
            continue;
          }
          trace = answer;
        } catch (error) {
          // Cancelled mid-request: `fetchJson` reports the abort as a network
          // failure, which it is not. The days read so far stand.
          if (cancelled()) {
            break;
          }
          if (!(error instanceof SourceError)) {
            throw error;
          }
          refusedInARow += 1;
          if (
            error.challenged ||
            error.kind === 'rate-limit' ||
            error.kind === 'network' ||
            refusedInARow >= ADSB_MAX_REFUSED_IN_A_ROW
          ) {
            stopWith(error, index);
          }
          refused.push({ day, error });
          continue;
        }
        if (trace === undefined) {
          continue;
        }
        const flights = splitTraceIntoFlights(traceFixes(trace));
        flights.forEach((fixes, index) => {
          const first = fixes[0];
          const last = fixes[fixes.length - 1];
          if (first === undefined || last === undefined) {
            return;
          }
          const ref = `${day}:${hex}:${index}`;
          this.cache.set(ref, { trace, fixes });
          const registration = trace.r === undefined ? undefined : normaliseRegistration(trace.r);
          candidates.push({
            ref,
            icaoHex: trace.icao ?? hex,
            ...(registration === undefined || registration === '' ? {} : { registration }),
            ...(trace.t === undefined || trace.t === '' ? {} : { aircraftType: trace.t }),
            startTime: new Date(first.tMs).toISOString(),
            endTime: new Date(last.tMs).toISOString(),
          });
        });
      }
    } finally {
      query.signal?.removeEventListener('abort', onAbort);
      stop.abort();
    }

    if (refused.length > 0) {
      const first = refused[0];
      const statuses = [...new Set(refused.map(({ error }) => error.status))]
        .filter((status) => status !== undefined)
        .map((status) => `HTTP ${String(status)}`)
        .join(', ');
      throw new SourceError(
        first?.error.kind ?? 'response',
        `${this.aggregator.label} refused ${dayRanges(refused.map(({ day }) => day))}${statuses === '' ? '' : ` (${statuses})`}.${unavailable.length > 0 ? ` No data for ${dayRanges(unavailable)}.` : ''} Other days were searched.`,
        {
          ...(first?.error.status === undefined ? {} : { status: first.error.status }),
          ...(first?.error.detail === undefined ? {} : { detail: first.error.detail }),
          partial: newestFirst(candidates),
          cause: first?.error,
        },
      );
    }
    if (unavailable.length > 0) {
      /*
       * Not a failure: an answer with a hole in it. `'not-found'` plus
       * `partial` is what the search screen shows as a note under the
       * results rather than as an error above them.
       */
      throw new SourceError(
        'not-found',
        `${this.aggregator.label} has no data for ${dayRanges(unavailable)}. ${unavailable.length === days.length ? 'No days in the range are held.' : 'Other days were searched.'}`,
        { status: 403, partial: newestFirst(candidates) },
      );
    }
    return newestFirst(candidates);
  }

  /**
   * One day of a search: the trace, `undefined` for "did not fly", or
   * `'unavailable'` for a day the globe does not hold at all.
   *
   * **A 403 from a globe is a missing day, not a refusal.** Found the hard
   * way: adsb.lol answered 403 for every day from 12 Oct 2025 — for ZK-DFH,
   * and for an Air NZ ATR that flew all day — typed straight into Safari as
   * well as through the relay, while days before it and recent days loaded.
   * A gap in what the live globe keeps, answered with 403 where a present day
   * with no file answers 404. It used to be retried after three seconds and
   * reported as the source "limiting automated requests", and three in a row
   * ended the search, which is how a gap of weeks hid every day after it.
   *
   * A Cloudflare challenge is still a refusal (the relay was stopped, not the
   * day missing), and a 429 is still a limit, retried once after a pause:
   * free source, bounded, one day — the no-retry rule is about metered calls.
   */
  private async fetchDayForSearch(
    hex: string,
    day: string,
    signal?: AbortSignal,
  ): Promise<AircraftTrace | undefined | 'unavailable'> {
    let error: SourceError;
    try {
      return await this.fetchDay(hex, day, signal);
    } catch (caught) {
      if (!(caught instanceof SourceError) || caught.kind === 'network') {
        throw caught;
      }
      error = caught;
    }

    if (error.status === 403 && !error.challenged) {
      return 'unavailable';
    }

    if (error.status === 429 && signal?.aborted !== true) {
      const waitMs = Math.min(
        (error.retryAfterS ?? ADSB_RETRY_DELAY_MS / 1000) * 1000,
        ADSB_MAX_RETRY_DELAY_MS,
      );
      await (this.options.sleep ?? ((ms) => new Promise((done) => setTimeout(done, ms))))(waitMs);
      try {
        return await this.fetchDay(hex, day, signal);
      } catch (caught) {
        if (!(caught instanceof SourceError) || caught.kind === 'network') {
          throw caught;
        }
        error = caught;
      }
    }

    const label = this.aggregator.label;
    const status = error.status === undefined ? '' : ` (HTTP ${String(error.status)})`;
    const message = error.challenged
      ? `${label}'s Cloudflare protection blocked the request for ${formatDay(day)}${status}. The relay was treated as a bot.`
      : error.kind === 'rate-limit'
        ? `${label} rate limit reached at ${formatDay(day)}${status}. Try again later or with a shorter range.`
        : `${label} refused the request for ${formatDay(day)}${status}.`;
    throw new SourceError(error.kind, message, {
      ...(error.status === undefined ? {} : { status: error.status }),
      ...(error.detail === undefined ? {} : { detail: error.detail }),
      challenged: error.challenged,
      cause: error,
    });
  }

  /** @param ref  `<YYYY-MM-DD>:<hex>:<segment index>`, as `search` produced it. */
  async fetch(ref: string): Promise<NormalisedTrack> {
    const [day, hex, indexText] = ref.split(':');
    const index = Number(indexText);
    if (day === undefined || hex === undefined || !Number.isInteger(index)) {
      throw new SourceError(
        'unsupported',
        `"${ref}" is not an ${this.aggregator.label} reference.`,
      );
    }

    let cached = this.cache.get(ref);
    if (cached === undefined) {
      // The cache is per-session, so a pilot returning to a candidate list after
      // a reload costs one more download — and nothing else.
      const trace = await this.fetchDay(hex, day);
      if (trace === undefined) {
        throw new SourceError(
          'not-found',
          `${this.aggregator.label} has no track for ${hex} on ${day}.`,
        );
      }
      const fixes = splitTraceIntoFlights(traceFixes(trace))[index];
      if (fixes === undefined) {
        throw new SourceError(
          'not-found',
          `${this.aggregator.label}'s file for ${hex} on ${day} no longer holds a flight at that position.`,
        );
      }
      cached = { trace, fixes };
      this.cache.set(ref, cached);
    }

    const registration =
      cached.trace.r === undefined ? undefined : normaliseRegistration(cached.trace.r);

    const parsed: ParsedTrack = {
      source: 'adsb',
      // `onGround` has done its job in the splitter and is not part of a fix.
      points: cached.fixes.map(({ onGround: _onGround, ...point }) => point),
      altitudeRef: 'baro',
      ...(registration === undefined || registration === '' ? {} : { registration }),
      warnings: [],
      notes: [
        `${this.aggregator.label} trace for ${hex} on ${day}: ${cached.fixes.length} positions.`,
        `Flight ${index + 1} of the day's file, split on coverage gaps and time on the ground.`,
      ],
    };

    return normaliseTrack(parsed, {
      sourceRef: `${this.aggregator.id}:${ref}`,
      ...(this.options.importedAt === undefined ? {} : { importedAt: this.options.importedAt }),
      ...(this.options.newId === undefined ? {} : { id: this.options.newId() }),
    });
  }

  estimateCost(operation: 'search' | 'fetch', count: number): CostEstimate {
    if (operation === 'search') {
      const days = Math.max(1, Math.ceil(count));
      return {
        calls: days,
        credits: 0,
        description: `${days} ${days === 1 ? 'request' : 'requests'}, one per day — ${this.aggregator.label} publishes a file per aircraft per day. Free, and it downloads the tracks as it goes, so picking one afterwards costs nothing more. ${this.aggregator.terms}`,
      };
    }
    return {
      calls: 0,
      credits: 0,
      description: `Free, and already downloaded by the search. ${this.aggregator.terms}`,
    };
  }
}
